import React, { useState } from 'react';
import { Users, Lightbulb, Globe, ChevronDown, ChevronRight, Target, BookOpen } from 'lucide-react';
import { useTranslation } from '../../contexts/TranslationContext';
import { Differentiation } from '../../types';

interface DifferentiationDisplayProps {
  differentiation: Differentiation;
}

export const DifferentiationDisplay: React.FC<DifferentiationDisplayProps> = ({
  differentiation
}) => {
  const { t } = useTranslation();
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    supports: false,
    extensions: false,
    ell: false
  });

  const hasSupports = differentiation.supports && Object.keys(differentiation.supports).length > 0;
  const hasExtensions = differentiation.extensions && Object.keys(differentiation.extensions).length > 0;
  const hasEll = differentiation.ell && (
    (differentiation.ell.sentence_frames && differentiation.ell.sentence_frames.length > 0) ||
    (differentiation.ell.icons && differentiation.ell.icons.length > 0)
  );

  if (!hasSupports && !hasExtensions && !hasEll) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
        <div className="text-center py-8">
          <Users className="w-16 h-16 mx-auto mb-4 text-gray-300" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('differentiationDisplay.differentiationStrategies')}</h3>
          <p className="text-sm text-gray-600">{t('differentiationDisplay.noStrategiesConfigured')}</p>
          <p className="text-xs text-gray-500 mt-1">{t('differentiationDisplay.configureStrategiesInEditor')}</p>
        </div>
      </div>
    );
  }

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const SectionHeader = ({ 
    id, 
    title, 
    icon: Icon, 
    count, 
    color 
  }: { 
    id: string; 
    title: string; 
    icon: any; 
    count: number; 
    color: string; 
  }) => (
    <button
      onClick={() => toggleSection(id)}
      className={`w-full flex items-center justify-between p-4 rounded-lg transition-colors ${color} hover:bg-opacity-90`}
    >
      <div className="flex items-center space-x-3">
        <Icon className="w-5 h-5" />
        <span className="font-medium">{title}</span>
        <span className="bg-white bg-opacity-50 text-xs px-2 py-1 rounded-full font-medium">
          {count}
        </span>
      </div>
      {expandedSections[id] ? (
        <ChevronDown className="w-5 h-5" />
      ) : (
        <ChevronRight className="w-5 h-5" />
      )}
    </button>
  );

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-blue-100 rounded-lg">
            <Users className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{t('differentiationDisplay.differentiationStrategies')}</h3>
            <p className="text-sm text-gray-600">{t('differentiationDisplay.strategiesToSupportLearners')}</p>
          </div>
        </div>
      </div>

      <div className="divide-y divide-gray-100">
        {/* Supports Section */}
        {hasSupports && (
          <div>
            <SectionHeader
              id="supports"
              title={t('differentiationDisplay.supportsForStrugglingLearners')}
              icon={Target}
              count={Object.keys(differentiation.supports!).length}
              color="bg-green-50 text-green-800"
            />
            {expandedSections.supports && (
              <div className="p-4 bg-green-50">
                <div className="space-y-3">
                  {Object.entries(differentiation.supports!).map(([key, value]) => (
                    <div key={key} className="bg-white border border-green-200 rounded-lg p-4 shadow-sm">
                      <div className="flex items-start space-x-3">
                        <div className="p-1 bg-green-100 rounded-full mt-0.5">
                          <Target className="w-3 h-3 text-green-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-green-900 leading-relaxed">{value}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Extensions Section */}
        {hasExtensions && (
          <div>
            <SectionHeader
              id="extensions"
              title={t('differentiationDisplay.extensionsForAdvancedLearners')}
              icon={Lightbulb}
              count={Object.keys(differentiation.extensions!).length}
              color="bg-purple-50 text-purple-800"
            />
            {expandedSections.extensions && (
              <div className="p-4 bg-purple-50">
                <div className="space-y-3">
                  {Object.entries(differentiation.extensions!).map(([key, value]) => (
                    <div key={key} className="bg-white border border-purple-200 rounded-lg p-4 shadow-sm">
                      <div className="flex items-start space-x-3">
                        <div className="p-1 bg-purple-100 rounded-full mt-0.5">
                          <Lightbulb className="w-3 h-3 text-purple-600" />
                        </div>
                        <div className="flex-1">
                          <p className="text-sm text-purple-900 leading-relaxed">{value}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {/* ELL Support Section */}
        {hasEll && (
          <div>
            <SectionHeader
              id="ell"
              title={t('differentiationDisplay.ellSupport')}
              icon={Globe}
              count={(differentiation.ell?.sentence_frames?.length || 0) + (differentiation.ell?.icons?.length || 0)}
              color="bg-orange-50 text-orange-800"
            />
            {expandedSections.ell && (
              <div className="p-4 bg-orange-50">
                <div className="space-y-6">
                  {/* Sentence Frames */}
                  {differentiation.ell?.sentence_frames && differentiation.ell.sentence_frames.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <BookOpen className="w-4 h-4 text-orange-600" />
                        <h5 className="text-sm font-medium text-orange-900">{t('differentiationDisplay.sentenceFrames')}</h5>
                      </div>
                      <div className="space-y-2">
                        {differentiation.ell.sentence_frames.map((frame, index) => (
                          <div key={index} className="bg-white border border-orange-200 rounded-lg p-3 shadow-sm">
                            <div className="flex items-start space-x-3">
                              <div className="p-1 bg-orange-100 rounded-full mt-0.5">
                                <BookOpen className="w-3 h-3 text-orange-600" />
                              </div>
                              <div className="flex-1">
                                <p className="text-sm font-mono text-orange-900 leading-relaxed">{frame}</p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Visual Icons */}
                  {differentiation.ell?.icons && differentiation.ell.icons.length > 0 && (
                    <div className="space-y-3">
                      <div className="flex items-center space-x-2">
                        <Globe className="w-4 h-4 text-orange-600" />
                        <h5 className="text-sm font-medium text-orange-900">{t('differentiationDisplay.visualIcons')}</h5>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {differentiation.ell.icons.map((icon, index) => (
                          <span 
                            key={index} 
                            className="bg-white border border-orange-200 text-orange-800 px-3 py-2 rounded-lg text-sm font-medium shadow-sm"
                          >
                            {icon}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Footer */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-500">
          <span>{t('differentiationDisplay.useStrategiesToSupport')}</span>
          <div className="flex items-center space-x-1">
            <Users className="w-3 h-3" />
            <span>{t('differentiationDisplay.differentiationReady')}</span>
          </div>
        </div>
      </div>
    </div>
  );
};
